import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MediaUploader } from "@/components/MediaUploader";
import { api } from "@/lib/api";
import { toast } from "sonner";

const empty = { media: [], title: "", description: "", link_url: "", button_label: "Go To", order: 0, category_id: null };

export function CardDialog({ open, onOpenChange, card, categories = [], onSaved }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const isEdit = !!card;

  useEffect(() => {
    setForm(card ? { ...empty, ...card, media: card.media || [] } : empty);
  }, [card, open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim()) return toast.error("Title is required");
    setSaving(true);
    try {
      const payload = {
        media: form.media, title: form.title,
        description: form.description, link_url: form.link_url,
        button_label: form.button_label || "Go To",
        order: form.order || 0, category_id: form.category_id || null,
      };
      if (isEdit) await api.put(`/cards/${card.id}`, payload);
      else await api.post("/cards", payload);
      toast.success(isEdit ? "Item updated" : "Item added");
      onSaved();
      onOpenChange(false);
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F0F] border border-white/15 rounded-none max-w-lg max-h-[90vh] overflow-y-auto" data-testid="card-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase tracking-tight text-2xl">
            {isEdit ? "Edit Item" : "Add Item"}
          </DialogTitle>
          <DialogDescription className="sr-only">Add photos, videos, a title, description and affiliate link for this item.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <MediaUploader media={form.media} onChange={(m) => set("media", m)} label="Photos & Videos" />
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Title</Label>
            <Input data-testid="card-title-input" value={form.title} onChange={(e) => set("title", e.target.value)}
              className="bg-transparent border-white/15 rounded-none focus-visible:ring-[#FF3B30]" placeholder="Product name" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Category</Label>
            <Select value={form.category_id || "none"} onValueChange={(v) => set("category_id", v === "none" ? null : v)}>
              <SelectTrigger data-testid="card-category-select" className="bg-transparent border-white/15 rounded-none focus:ring-[#FF3B30]">
                <SelectValue placeholder="Uncategorized" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/15 rounded-none text-white">
                <SelectItem value="none">Uncategorized</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Description</Label>
            <Textarea data-testid="card-description-input" value={form.description} onChange={(e) => set("description", e.target.value)}
              className="bg-transparent border-white/15 rounded-none focus-visible:ring-[#FF3B30]" rows={3} placeholder="Why you recommend it" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Affiliate Link</Label>
            <Input data-testid="card-link-input" value={form.link_url} onChange={(e) => set("link_url", e.target.value)}
              className="bg-transparent border-white/15 rounded-none focus-visible:ring-[#FF3B30]" placeholder="https://..." />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-mono uppercase tracking-widest text-zinc-400">Button Label</Label>
            <Select value={form.button_label || "Go To"} onValueChange={(v) => set("button_label", v)}>
              <SelectTrigger data-testid="card-button-label-select" className="bg-transparent border-white/15 rounded-none focus:ring-[#FF3B30]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/15 rounded-none text-white">
                <SelectItem value="Go To">Go To</SelectItem>
                <SelectItem value="Landing Page">Landing Page</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <button data-testid="card-save-btn" onClick={save} disabled={saving}
            className="bg-white text-black font-bold px-6 py-3 uppercase tracking-wider text-sm hover:bg-[#FF3B30] hover:text-white transition-colors disabled:opacity-50">
            {saving ? "Saving..." : "Save"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
